<template>
	<div>
		<user-signup-modal v-if="showUserSignupModal" @close="closeSignupModal" />
		<user-invite-modal v-if="showUserInviteModal" @close="closeInviteModal" />
		<div class="stage">
			<div class="row ml-1">
				<h1 class="font-medium">Users</h1>
			</div>
			<div v-if="$global.hasUserRole(userRoles, 'users:create')" class="row mb-2">
				<div class="col-xs-12 col-sm-12 col-md-12">
					<ul class="btnList">
						<li>
							<a class="jmiBtn green withIcon" @click="signupUser()"
								><span class="fa-light fa-plus"></span>New User Signup</a
							>
						</li>
						<li>
							<a class="jmiBtn blue withIcon" @click="inviteUsers()"
								><span class="fa-light fa-envelope"></span>Invite Users</a
							>
						</li>
						<li>
							<a class="jmiBtn red withIcon" @click="forceUserSync()"
								><span class="fa-light fa-sync"></span>Force User Sync</a
							>
						</li>
					</ul>
				</div>
			</div>
			<div class="row margin-b20">
				<div class="col-xs-12 col-sm-6 col-md-6 col-lg-6">
					<p class="filter-label">Search</p>
					<div class="formGroup withIcon">
						<input v-model="searchTerm" placeholder="Search..." />
					</div>
				</div>
			</div>

			<div class="flex items-center justify-center pb-6 md:pb-4">
				<div
					class="relative flex w-full flex-wrap gap-2 self-center rounded-lg p-0.5 md:w-[800px] md:flex-nowrap"
				>
					<button
						v-for="option in statusTabs"
						:key="option.value"
						type="button"
						class="relative w-[calc(50%-0.25rem)] cursor-default whitespace-nowrap rounded-full border border-main px-4 py-2 text-sm font-medium transition-all active:scale-[0.98] dark:border-main md:w-1/4"
						:class="{
							'border-transparent bg-main text-white dark:border-transparent dark:bg-accentMain dark:text-black':
								activeTab === option.value,
							'bg-transparent text-black hover:bg-main/10 dark:text-light dark:hover:bg-offwhite/10':
								activeTab !== option.value
						}"
						@click="setActiveTab(option.value)"
					>
						<span class="hidden sm:block">{{ option.label }}</span>
						<span class="block sm:hidden">{{ option.shortLabel || option.label }}</span>
					</button>
				</div>
			</div>

			<data-view
				:columns="columns"
				:sort-column="sortColumn"
				:reverse="reverse"
				:loading="loading"
				:label="'Users'"
				:total-items="filteredUsers.length"
				:page-size="pageSize"
				:current-page="currentPage"
				:pages="[1]"
				@sort="setSort"
			>
				<template #table-view>
					<generic-table
						:items="filteredUsers"
						:columns="columns"
						item-type="users"
						base-route="users"
						:can-edit="$global.hasUserRole(userRoles, 'users:update')"
						:not-clickable="true"
						:view-mode="'table'"
						:mobile-layout="mobileLayout"
						:actions="userActions"
						:empty-state-config="{
							icon: 'fa-light fa-users',
							title: getEmptyStateTitle(),
							description: getEmptyStateDescription(),
							action: getEmptyStateAction()
						}"
					>
						<template #name="{ item }">
							<span
								:class="{ 'cursor-pointer hover:underline': !item.isPendingInvitation }"
								@mouseover="createTooltip($event, getUserDisplayName(item))"
								@click="!item.isPendingInvitation && routeTo('/users/' + item.id)"
							>
								{{ getUserDisplayName(item) }}
							</span>
						</template>
						<template #email="{ item }">
							<span @mouseover="createTooltip($event, getUserEmail(item))">
								{{ getUserEmail(item) }}
							</span>
						</template>
						<template #provider="{ item }">
							<span @mouseover="createTooltip($event, userProvider(item))">
								{{ userProvider(item) }}
							</span>
						</template>
						<template #status="{ item }">
							<span
								class="rounded px-1.5 py-0.5 text-xs font-normal"
								:class="getUserStatusBadgeClass(item)"
							>
								{{ getUserStatus(item) }}
							</span>
						</template>
						<template #reseller="{ item }">
							<span @mouseover="createTooltip($event, item.reseller?.companyName)">
								{{ item.reseller?.companyName || 'Unassigned' }}
							</span>
						</template>
						<template #tenants="{ item }">
							<span
								@mouseover="createTooltip($event, item.tenants?.map((t) => t.companyName).join(', '))"
							>
								{{ item.tenants?.map((t) => t.companyName).join(', ') || 'None' }}
							</span>
						</template>
						<template #accounts="{ item }">
							<span @mouseover="createTooltip($event, item.accounts?.map((a) => a.name).join(', '))">
								{{ item.accounts?.map((a) => a.name).join(', ') || 'None' }}
							</span>
						</template>
						<template #createdAt="{ item }">
							<span @mouseover="createTooltip($event, $global.dateFormat(item.createdAt))">
								{{ $global.dateFormat(item.createdAt) }}
							</span>
						</template>
					</generic-table>
				</template>
			</data-view>

			<transition-fade easing="ease-in">
				<div v-show="loading" class="row margin-b20 center-xs center-md center-lg">
					<div v-show="loading" class="loader"></div>
				</div>
			</transition-fade>
		</div>
	</div>
</template>

<script>
import usersApi from '../scripts/users/users.api'
import { router } from '../router'
import UserInviteModal from '../components/UserInviteModal.vue'
import UserSignupModal from '../components/UserSignupModal.vue'
import DataView from '../components/common/DataView.vue'
import GenericTable from '../components/common/GenericTable.vue'

export default {
	components: {
		UserInviteModal,
		UserSignupModal,
		DataView,
		GenericTable
	},
	inject: ['user', 'tenants', 'userRoles'],
	data() {
		return {
			users: [],
			invitations: [],
			searchTerm: '',
			sortColumn: '',
			reverse: false,
			loading: true,
			showUserSignupModal: false,
			showUserInviteModal: false,
			searchOptions: [],
			selectedSearchFilters: [],
			totalUsers: 0,
			currentPage: 1,
			pageSize: 10,
			activeTab: 'all',
			statusTabs: [
				{ value: 'all', label: 'All Users', shortLabel: 'All' },
				{ value: 'active', label: 'Active Users', shortLabel: 'Active' },
				{ value: 'disabled', label: 'Disabled Users', shortLabel: 'Disabled' },
				{ value: 'pending', label: 'Pending Invitations', shortLabel: 'Pending' }
			],
			columns: [
				{
					key: 'name',
					label: 'Name',
					sortable: true,
					class: 'col-xs-12 col-sm-2 col-md-2'
				},
				{
					key: 'email',
					label: 'Email',
					sortable: true,
					class: 'col-xs-12 col-sm-2 col-md-2'
				},
				{
					key: 'provider',
					label: 'Provider',
					sortable: true,
					class: 'col-xs-12 col-md-1 hideTabletLandscape'
				},
				{
					key: 'status',
					label: 'Status',
					class: 'col-xs-12 col-sm-1 col-md-1'
				},
				{
					key: 'reseller',
					label: 'Reseller',
					class: 'col-xs-12 col-sm-1 hideTablet',
					permission: 'reseller'
				},
				{
					key: 'tenants',
					label: 'Tenants',
					class: 'col-xs-12 col-sm-2'
				},
				{
					key: 'accounts',
					label: 'Accounts',
					class: 'col-xs-12 col-sm-2'
				},
				{
					key: 'createdAt',
					label: 'Date',
					sortable: true,
					class: 'col-xs-12 col-md-1 hideTabletLandscape'
				}
			],
			mobileLayout: {
				header: {
					key: 'name',
					label: 'Name'
				},
				mainFields: [
					{ key: 'email', label: 'Email' },
					{ key: 'status', label: 'Status' },
					{ key: 'provider', label: 'Provider' },
					{ key: 'tenants', label: 'Tenants' }
				]
			},
			userActions: [
				{
					handler: (user) => this.routeTo('/users/' + user.id),
					icon: 'fa-light fa-pencil',
					label: 'Edit',
					show: (user) => this.$global.hasUserRole(this.userRoles, 'users:update')
				},
				{
					handler: (user) => console.log('Disable User', user),
					icon: 'fa-light fa-ban',
					label: 'Disable User',
					show: (user) =>
						this.getUserStatus(user) === 'Active' &&
						this.$global.hasUserRole(this.userRoles, 'users:update')
				},
				{
					handler: (user) => console.log('Enable User', user),
					icon: 'fa-light fa-check',
					label: 'Enable User',
					show: (user) =>
						this.getUserStatus(user) === 'Disabled' &&
						this.$global.hasUserRole(this.userRoles, 'users:update')
				},
				{
					handler: (user) => console.log('Reset Password', user),
					icon: 'fa-light fa-key',
					label: 'Reset Password',
					show: (user) => this.$global.hasUserRole(this.userRoles, 'users:update')
				},
				{
					handler: (user) => console.log('Delete User', user),
					icon: 'fa-light fa-trash',
					label: 'Delete User',
					show: (user) =>
						this.getUserStatus(user) !== 'Pending' &&
						this.$global.hasUserRole(this.userRoles, 'users:update')
				}
			]
		}
	},

	computed: {
		filteredUsers() {
			let filtered = []

			if (this.activeTab === 'pending') {
				filtered = this.invitations.map((invitation) => ({
					id: invitation.email,
					email: invitation.email,
					invitation: invitation,
					isPendingInvitation: true,
					auth0: {
						email: invitation.email,
						email_verified: false
					}
				}))
			} else {
				filtered = [...this.users]

				if (this.activeTab && this.activeTab !== 'all') {
					filtered = filtered.filter((user) => {
						const status = this.getUserStatus(user).toLowerCase()
						return status === this.activeTab.toLowerCase()
					})
				}
			}

			// Apply search filter
			if (this.selectedSearchFilters && this.selectedSearchFilters.length > 0) {
				filtered = filtered.filter((user) => {
					return this.selectedSearchFilters.some((filter) => {
						const searchValue = filter.toLowerCase()
						return (
							user.id.toLowerCase().includes(searchValue) ||
							this.getUserDisplayName(user).toLowerCase().includes(searchValue) ||
							this.getUserEmail(user).toLowerCase().includes(searchValue)
						)
					})
				})
			} else if (this.searchTerm) {
				const searchLower = this.searchTerm.toLowerCase()
				filtered = filtered.filter((u) => {
					return (
						u.id.toLowerCase().includes(searchLower) ||
						this.getUserDisplayName(u).toLowerCase().includes(searchLower) ||
						this.getUserEmail(u).toLowerCase().includes(searchLower)
					)
				})
			}

			return filtered
		}
	},

	watch: {
		'$route.query': {
			handler(newQuery) {
				const tab = newQuery.tab
				if (tab && this.statusTabs.some((t) => t.value === tab)) {
					this.activeTab = tab
				}
			},
			deep: true,
			immediate: true
		}
	},

	created() {
		try {
			// Initialize tab from route query
			const tab = this.$route.query.tab
			if (tab && this.statusTabs.some((t) => t.value === tab)) {
				this.activeTab = tab
			}

			this.refreshUsers()
		} catch (e) {
			this.$swal({
				title: 'Error loading users',
				icon: 'error',
				confirmButtonText: 'Confirm',
				text: 'Unknown error occurred, please try again. If the issue persists, please raise a support ticket.'
			}).then(() => {
				router.push('/')
			})
			console.error(e)
		}
	},

	methods: {
		closeSignupModal() {
			this.showUserSignupModal = false
			this.refreshUsers()
		},
		closeInviteModal() {
			this.showUserInviteModal = false
			this.refreshUsers()
		},
		routeTo(routeName) {
			router.push(routeName)
		},
		setSort(column, reverse) {
			if (this.sortColumn !== column) this.reverse = false
			else this.reverse = reverse

			this.sortColumn = column

			this.refreshUsers()
		},
		signupUser() {
			this.showUserSignupModal = true
		},
		inviteUsers() {
			this.showUserInviteModal = true
		},
		async forceUserSync() {
			const result = await this.$swal({
				title: 'Force User Sync',
				text: 'This will sync all users with Auth0. This may take a few minutes. Continue?',
				icon: 'question',
				showCancelButton: true,
				confirmButtonText: 'Start Sync',
				cancelButtonText: 'Cancel'
			})

			if (result.isConfirmed) {
				try {
					this.loading = true
					await usersApi.forceUserSync()
					this.$swal({
						title: 'Sync Started',
						icon: 'success',
						text: 'User sync has been initiated. This may take a few minutes to complete.',
						confirmButtonText: 'OK'
					})
					this.refreshUsers()
				} catch (err) {
					console.error('Error starting user sync:', err)
					this.$swal({
						title: 'Error',
						icon: 'error',
						text: 'Failed to start user sync',
						confirmButtonText: 'OK'
					})
				} finally {
					this.loading = false
				}
			}
		},
		setActiveTab(tab) {
			this.activeTab = tab
			this.$router.push({ query: { ...this.$route.query, tab } })
		},
		getUserDisplayName(user) {
			if (user.isPendingInvitation) {
				return user.invitation?.email || user.email || 'Pending User'
			}
			if (user.id.indexOf('@clients') > -1) return 'API User'
			if (user.auth0?.name) return user.auth0.name
			if (user.auth0?.nickname) return user.auth0.nickname
			if (user.auth0?.email) return user.auth0.email
			return user.id || 'Unknown User'
		},
		getUserEmail(user) {
			if (user.isPendingInvitation) {
				return user.invitation?.email || user.email || 'No email'
			}
			if (user.id.indexOf('@clients') > -1) return user.id
			if (user.auth0?.email) return user.auth0.email
			return user.id || 'No email'
		},
		userProvider(user) {
			if (user.isPendingInvitation) return 'Invitation'
			if (!user.auth0?.identities?.[0]) return 'Unknown'
			const provider = user.auth0.identities[0].provider
			if (provider === 'google-oauth2') return 'Google'
			if (provider === 'windowslive') return 'Microsoft'
			if (provider === 'linkedin') return 'LinkedIn'
			if (provider === 'auth0') return 'Username / Password'
			return 'Other'
		},
		getUserStatus(user) {
			if (user.isPendingInvitation) return 'Pending'
			if (user.auth0?.blocked) return 'Disabled'
			if (user.auth0?.email_verified === false) return 'Pending'
			return 'Active'
		},
		getUserStatusBadgeClass(user) {
			const status = this.getUserStatus(user)
			switch (status.toLowerCase()) {
				case 'active':
					return 'bg-green-600 text-white'
				case 'disabled':
					return 'bg-red-600 text-white'
				case 'pending':
					return 'bg-yellow-500 text-gray-900'
				default:
					return 'bg-gray-500 text-white'
			}
		},

		async refreshUsers() {
			try {
				this.loading = true
				let params = {
					order: this.sortColumn
				}

				if (!this.reverse) params.dir = 'asc'

				// Load both users and invitations in parallel
				const [usersResponse, invitationsResponse] = await Promise.all([
					usersApi.getAll(params),
					usersApi.getInvitations().catch((err) => {
						console.warn('Failed to load invitations:', err)
						return { data: [] }
					})
				])

				this.users = usersResponse.data || []
				this.invitations = invitationsResponse.data || []
				this.totalUsers = this.users.length + this.invitations.length

				const maxPages = Math.ceil(this.totalUsers / this.pageSize) || 1
				if (this.currentPage > maxPages) {
					this.currentPage = 1
					this.refreshUsers()
					return
				}

				this.updateSearchOptions()
			} catch (err) {
				console.error(err)
			} finally {
				this.loading = false
			}
		},
		handleSearchChange(value) {
			this.selectedSearchFilters = Array.isArray(value) ? value : value ? [value] : []
		},
		updateSearchOptions() {
			const options = new Set()

			// Add users to search options
			this.users.forEach((user) => {
				const displayName = this.getUserDisplayName(user)
				const email = this.getUserEmail(user)

				if (user.id) options.add(user.id)
				if (displayName && displayName !== 'Unknown User') options.add(displayName)
				if (email && email !== 'No email') options.add(email)
			})

			this.invitations.forEach((invitation) => {
				if (invitation.email) options.add(invitation.email)
			})

			this.searchOptions = Array.from(options).map((option) => ({
				value: option,
				label: option
			}))
		},
		getEmptyStateTitle() {
			const hasSearchTerm =
				this.searchTerm || (this.selectedSearchFilters && this.selectedSearchFilters.length > 0)

			if (hasSearchTerm) {
				switch (this.activeTab) {
					case 'active':
						return 'No active users found'
					case 'disabled':
						return 'No disabled users found'
					case 'pending':
						return 'No pending invitations found'
					default:
						return 'No users found'
				}
			}

			switch (this.activeTab) {
				case 'active':
					return 'No active users yet'
				case 'disabled':
					return 'No disabled users'
				case 'pending':
					return 'No pending invitations'
				default:
					return 'No users yet'
			}
		},
		getEmptyStateDescription() {
			const hasSearchTerm =
				this.searchTerm || (this.selectedSearchFilters && this.selectedSearchFilters.length > 0)

			if (hasSearchTerm) {
				return 'Try adjusting your search criteria or clear the search to see all users.'
			}

			switch (this.activeTab) {
				case 'active':
					return 'Start by adding your first active user to the system.'
				case 'disabled':
					return 'No users have been disabled yet.'
				case 'pending':
					return 'Send your first user invitation to get started.'
				default:
					return 'Start by adding your first user to the system.'
			}
		},
		getEmptyStateAction() {
			if (this.$global.hasUserRole(this.userRoles, 'users:create') && this.activeTab !== 'pending') {
				return {
					handler: () => this.signupUser(),
					label: 'Add New User',
					icon: 'fa-light fa-plus'
				}
			} else if (this.$global.hasUserRole(this.userRoles, 'users:create') && this.activeTab === 'pending') {
				return {
					handler: () => this.inviteUsers(),
					label: 'Invite Users',
					icon: 'fa-light fa-envelope'
				}
			}
			return null
		},

		createTooltip(event, text) {
			if (text) {
				event.target.setAttribute('title', text)
			}
		}
	}
}
</script>
